import { autoinject, bindable, computedFrom, transient } from "aurelia-framework";
import { Claims } from "../../../../../constants/authentication/claims";
import { RightShipDialogServiceConstants } from "../../../../../constants/basics/rightship-dialog.constants";
import { CompRightStoreProductModel } from "../../../../../models/rightstore/rightstore.models";
import { GetBuRuleCollectionModel, RuleCollectionRightStoreSetupClientModel } from "../../../../../models/rules/rules.models";
import { BannerGrouping, VesselDetailModel } from "../../../../../models/vessels/vessel.models";
import { VetClientModel, VetOrderClientModel, VettingType } from "../../../../../models/vetting/vetting.models";
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import { UserClientService } from "../../../../../services/authentication/user-client.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService, NotificationType } from "../../../../../services/basics/notification-client.service";
import { CustomerService } from "../../../../../services/modules/customer-client.service";
import { CustomerService as CommonCustomerService } from "@rightship-tech/common-ui/services/basic/customer-client.service";
import { RulesClientService } from "../../../../../services/rules/rules-client.service";
import { VetOrderService } from "../../../../../services/vetting/vet-order.service";
import { Messages } from "../../../../../configuration/event-messages.constants";
import { EventAggregator } from "aurelia-event-aggregator";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";

type PendingVetOrderModel = {
    order: VetOrderClientModel;
    product: CompRightStoreProductModel;
};

@autoinject
@transient()
export class ChooseVetScreeningDialog {
    private _isTest: boolean = false;

    canRequestVet: boolean = false;
    canRequestAutoScreening: boolean = false;
    impersonateBUForRequest: boolean = false;
    rightShipUser: boolean = false;
    customerId: number;
    businessUnitId: number;
    ruleCollections: GetBuRuleCollectionModel[];
    allRightStoreSetups: RuleCollectionRightStoreSetupClientModel[];
    rightStoreSetups: RuleCollectionRightStoreSetupClientModel[];
    pendingOrders: PendingVetOrderModel[];
    isRightStoreEnabled: boolean = false;

    @bindable
    model: ChooseVetScreeningDialogModel;

    @computedFrom("_isTest")
    get isTest(): boolean {
        return this._isTest
    }

    set isTest(value: boolean) {
        this._isTest = value;

        if (!value) //reset customer id, to show warning again
        {
            this.customerId = null;
            this.businessUnitId = null;
        }
    }

    @computedFrom("ruleCollections", "ruleCollections.length")
    get vetRuleCollections(): GetBuRuleCollectionModel[] {
        return (this.ruleCollections ?? []).filter(x => x.vettingTypeId == VettingType.FullVet);
    }

    @computedFrom("ruleCollections", "ruleCollections.length")
    get screeningRuleCollections(): GetBuRuleCollectionModel[] {
        return (this.ruleCollections ?? []).filter(x => x.vettingTypeId == VettingType.AutoScreening);
    }

    @computedFrom("businessUnitId", "_isTest", "impersonateBUForRequest", "ruleCollections", "ruleCollections.length", "model", "model.vettingType")
    get showVetOptions() {
        return this.businessUnitId > 0 && ((this._isTest && this.impersonateBUForRequest) || !this.isTest) && this.canRequestVet
            && (!this.model.vettingType || this.model.vettingType == VettingType.FullVet);
    }

    @computedFrom("businessUnitId", "_isTest", "impersonateBUForRequest", "ruleCollections", "ruleCollections.length", "model", "model.vettingType")
    get showScreeningOptions() {
        return this.businessUnitId > 0 && ((this._isTest && this.impersonateBUForRequest) || !this.isTest) && this.canRequestAutoScreening
            && (!this.model.vettingType || this.model.vettingType == VettingType.AutoScreening);
    }

    constructor(private dialogClientService: DialogClientService, private userClaimsService: UserClaimsService, private commonCustomerService:CommonCustomerService,
        private userClientService: UserClientService, private customerService: CustomerService, private ruleService: RulesClientService,
        private notificationClientService: NotificationClientService, private vetOrderService: VetOrderService, private eventAggregator: EventAggregator,
        private dialogService: DialogClientService, private configService: ConfigService) {
        this.impersonateBUForRequest = this.userClaimsService.hasPermission(Claims.ImpersonateBUForRequestingVet);
        this.canRequestVet = this.userClaimsService.hasPermission(Claims.CanRequestVet);
        this.canRequestAutoScreening = this.userClaimsService.hasPermission(Claims.CanRequestAutoScreening);
        this.rightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
        this.isRightStoreEnabled = this.userClaimsService.hasPermission(Claims.IsRightStoreEnabled);
    }

    async activate(model: ChooseVetScreeningDialogModel) {
        this.model = model;

        this.customerId = this.userClientService.currentUser.settings.customerId;
        if (this.customerId == null || this.customerId <= 0) {
            this.customerId = await this.commonCustomerService.getCustomerId();
        }

        this.businessUnitId = this.userClientService.currentUser.settings.businessUnitId;
        if (this.businessUnitId == null || this.businessUnitId <= 0) {
            this.businessUnitId = await this.commonCustomerService.getBusinessUnitId();
        }

        //load assigned rule collections
        if (this.businessUnitId > 0) {
            this.ruleCollections = await this.ruleService.getBuRuleCollections(this.businessUnitId, null, false);
        }

        await this.loadRightStoreSetups();

        await this.getPendingOrders();
    }

    screening(ruleCollection: GetBuRuleCollectionModel) {
        this.eventAggregator.publish(Messages.buttonClick, { "buttonName": "Screening", "functionName": "screening" });
        this.eventAggregator.publish(Messages.cardClick, { "cardCategory": "Screening", "card": ruleCollection.displayName });

        this.dialogClientService.hide();

        //This timeout is here because of the animations when hiding & showing 2 dialogs at once
        setTimeout(() => {
            this.showRequestAutoScreeningDialog(ruleCollection);
        }, 300);
    }

    vet(ruleCollection: GetBuRuleCollectionModel) {
        this.eventAggregator.publish(Messages.buttonClick, { "buttonName": "Vetting", "functionName": "vet" });
        this.eventAggregator.publish(Messages.cardClick, { "cardCategory": "Vetting", "card": ruleCollection.displayName });

        this.dialogClientService.hide();

        //This timeout is here because of the animations when hiding & showing 2 dialogs at once
        setTimeout(() => {
            this.showRequestVetDialog(ruleCollection);
        }, 300);
    }

    cancel() {
        this.dialogClientService.hide();
    }

    buChanged($event) {
        if (this.isTest && $event && $event.detail && $event.detail.item && $event.detail.item.id > 0) {
            //reload assigned vets/screening rule collections for bu
            if (this.businessUnitId > 0) {
                this.ruleService.getBuRuleCollections(this.businessUnitId, null, false).then(r => {
                    this.ruleCollections = r;
                })
                    .catch(e => {
                        console.error(e);
                        this.notificationClientService.show("Error", "Failed to get vet/auto screenings for business unit", NotificationType.Error);
                    });
            }
        }
    }

    showRequestVetDialog(ruleCollection: GetBuRuleCollectionModel) {

        const showDialog = () => {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.RequestVetDialog,
                {
                    vesselId: this.model.vessel.vesselId,
                    vessel: {
                        vetRequiresMoreInformationEmails: this.model.vessel.vetRequiresMoreInformationEmails,
                        documentOfComplianceEmail: this.model.vessel.documentOfComplianceEmail,
                        cargoTypeCategoryId: this.model.vessel.cargoTypeCategoryId,
                        vesselId: this.model.vessel.vesselId,
                        internationalMaritimeOrganizationNumber: this.model.vessel.internationalMaritimeOrganizationNumber,
                        name: this.model.vessel.name
                    },
                    onConfirm: this.model.onConfirm,
                    parentVetId: this.model.parentVetId,
                    vet: this.model.vet,
                    linkFullVetCheck: this.model.linkFullVetCheck,
                    ruleCollectionId: ruleCollection.ruleCollectionId,
                    viewModel: ruleCollection.ruleCollectionInputVM,
                    customerId: this.customerId,
                    businessUnitId: this.businessUnitId,
                    isTest: this.isTest
                }, true, false, false, 'right');
        };

        if (this.model.groupedBanners && !this.rightShipUser) {

            const banners = this.model.groupedBanners.filter(x =>
                x.internalName == "CustomerRestricted" ||
                x.internalName == "restricted");

            if (banners.length > 0) {
                this.dialogClientService.show(RightShipDialogServiceConstants.URLs.ConfirmListRequestVetDialog,
                    {
                        bannerGroups: this.model.groupedBanners,
                        onContinue: showDialog
                    }, true, false, false, 'right');
            }
            else {
                showDialog();
            }
        }
        else {
            showDialog();
        }

    }

    showRequestAutoScreeningDialog(ruleCollection: GetBuRuleCollectionModel) {
        const showDialog = () => {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.RequestAutoScreeningDialog,
                {
                    ruleCollectionInputVM: ruleCollection.ruleCollectionInputVM,
                    vesselId: this.model.vessel.vesselId,
                    vessel: {
                        cargoTypeCategoryId: this.model.vessel.cargoTypeCategoryId,
                        vesselId: this.model.vessel.vesselId,
                        internationalMaritimeOrganizationNumber: this.model.vessel.internationalMaritimeOrganizationNumber,
                        name: this.model.vessel.name
                    },
                    onConfirm: this.model.onConfirm,
                    ruleCollectionId: ruleCollection.ruleCollectionId,
                    customerId: this.customerId,
                    businessUnitId: this.businessUnitId,
                    isTest: this.isTest
                }, true, false, false, 'right');
        };

        if (this.model.groupedBanners && !this.rightShipUser) {
            const banners = this.model.groupedBanners.filter(x =>
                x.internalName == "CustomerRestricted" ||
                x.internalName == "restricted");

            if (banners.length > 0) {
                this.dialogClientService.show(RightShipDialogServiceConstants.URLs.ConfirmListRequestVetDialog,
                    {
                        bannerGroups: this.model.groupedBanners,
                        onContinue: showDialog
                    }, true, false, false, 'right');
            }
            else {
                showDialog();
            }
        }
        else {
            showDialog();
        }
    }

    async loadRightStoreSetups() {
        const response = await this.ruleService.getRuleCollectionRightStoreSetups();

        this.allRightStoreSetups = response.rightStoreSetups;
        this.rightStoreSetups = response.rightStoreSetups;
    }

    async getPendingOrders() {
        try {
            const response = await this.vetOrderService.getPendingOrders();

            if (response && response.pendingOrders) {
                const ruleCollectionIds = response.pendingOrders.map(x => x.ruleCollectionId);
                //filter out setups which have pending orders
                this.rightStoreSetups = this.allRightStoreSetups.filter(x => ruleCollectionIds.indexOf(x.ruleCollectionId) == -1);

                const pendingRsSetups: { [id: number]: RuleCollectionRightStoreSetupClientModel } = Object.assign({}, ...this.allRightStoreSetups.filter(x => ruleCollectionIds.indexOf(x.ruleCollectionId) > -1).map(x => ({ [x.ruleCollectionId]: x })));
                this.pendingOrders = response.pendingOrders.map(x => {
                    return {
                        order: x,
                        product: pendingRsSetups[x.ruleCollectionId] ? this.getProductModel(pendingRsSetups[x.ruleCollectionId]) : null
                    };
                }).filter(x => x.order && x.product);
            }
            else {
                this.rightStoreSetups = this.allRightStoreSetups;
            }
        }
        catch (e) {
            this.notificationClientService.show("Error", "Failed to get pending orders", NotificationType.Error);
            console.error(e);
        }
    }

    public getProductModel(setup: RuleCollectionRightStoreSetupClientModel) {
        return {
            description: setup.description,
            imageUrl: setup.imageUrl,
            name: setup.ruleCollectionName,
            url: setup.rightStoreProductUrl,
            tags: setup.tags,
            isComingSoon: setup.isComingSoon,
            expiry: null,
            showInPlatform: true
        }
    }

    async onProductClicked(product: RuleCollectionRightStoreSetupClientModel) {
        if (product && product.rightStoreProductUrl) {
            const queryParams = this.configService.config.environment.toLowerCase() == "production" ?
                `vesselId=${this.model.vessel.vesselId}` : `vesselId=${this.model.vessel.vesselId}&env=${this.configService.config.environment}`;
            const customerModel = this.customerId > 0 ? await this.customerService.getCustomer(this.customerId) : null;
            this.dialogService.show(RightShipDialogServiceConstants.URLs.RightstoreCheckoutDialog,
                {
                    userName: this.userClientService.currentUser.email,
                    customerNumber: this.businessUnitId,
                    productNumber: product.rightStoreProductCode,
                    queryParams: queryParams,
                    canCustomerInvoice: customerModel?.customer?.canInvoice ?? true,
                }, true, false, false, 'right');

            this.eventAggregator.publish(Messages.buttonClick, { "buttonName": product.ruleCollectionName, "functionName": "onProductClicked" });
            this.eventAggregator.publish(Messages.cardClick, { "cardCategory": "Rightstore Product", "card": product.ruleCollectionName });
        }
    }

    onPendingOrderClicked(item: PendingVetOrderModel) {
        if (item && item.order && item.order.orderGuid) {
            const rightStoreUri = this.configService.config.urls.rightStoreApiUrl;
            const url = `${rightStoreUri + (rightStoreUri.endsWith("/") ? "" : "/")}api/order/${item.order.orderGuid}` + (this.model.vessel ? `?vesselId=${this.model.vessel.vesselId}` : "");
            window.open(url, "_blank");
        }
    }

    navigateToRightStoreProducts() {
        window.open(this.configService.config.urls.contactForFullSubscriptionUrl, "_blank");
    }
}

export interface ChooseVetScreeningDialogModel {
    vessel: VesselDetailModel;
    onConfirm?: () => void;
    vettingType?: number;
    parentVetId?: number;
    vet?: VetClientModel;
    linkFullVetCheck?: boolean;
    groupedBanners?: BannerGrouping[];
}